import { ApiError } from '@core/api/apiError';

/**
 * Загрузка профиля с повтором (K-041). Ошибка загрузки — не «гость с нулём»: ProfileContext хранит её отдельно,
 * а экраны показывают «Не удалось загрузить баланс» с «Повторить». Временные сбои — 429 (общий лимит по IP),
 * 5xx и сеть — повторяются с нарастающей задержкой; прочие ошибки (400/403/404) — сразу.
 */

/** Задержки перед повторами, мс: 1 + 3 + 8 с — около 12 с до экрана ошибки. */
export const PROFILE_RETRY_DELAYS_MS = [1000, 3000, 8000] as const;

/** Временный сбой, который стоит повторить: 429, 5xx, сеть или ответ не JSON. */
export function isRetryableProfileError(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.status === undefined) return true;
    return err.status === 429 || err.status >= 500;
  }
  // Не ApiError — сбой сети или fetch до ответа сервера.
  return true;
}

/** Клиента для этой сессии нет: uuid от прошлой сессии или другого заведения, клиент удалён (PWA → выход). */
export function isProfileGoneError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 403 || err.status === 404);
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * fetch() с повторами временных сбоев. shouldContinue() — false, если запрос устарел (сменился клиент или начат
 * более свежий): тогда повторы прекращаются, а последняя ошибка пробрасывается.
 */
export async function loadProfileWithRetry<T>(
  fetch: () => Promise<T>,
  opts: { delays?: readonly number[]; sleep?: (ms: number) => Promise<void>; shouldContinue?: () => boolean } = {},
): Promise<T> {
  const delays = opts.delays ?? PROFILE_RETRY_DELAYS_MS;
  const sleep = opts.sleep ?? defaultSleep;
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetch();
    } catch (err) {
      const canRetry = attempt < delays.length && isRetryableProfileError(err) && (opts.shouldContinue?.() ?? true);
      if (!canRetry) throw err;
      await sleep(delays[attempt]);
    }
  }
}
